import { AfterViewInit, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Platform } from '@angular/cdk/platform';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { filter, takeUntil, tap } from 'rxjs/operators';

import { FuseConfigService } from '@fuse/services/config.service';
import { FuseNavigationService } from '@fuse/components/navigation/navigation.service';
import { FuseSidebarService } from '@fuse/components/sidebar/sidebar.service';
import { FuseSplashScreenService } from '@fuse/services/splash-screen.service';

import { navigation } from 'app/navigation/navigation';
import { MenuService } from './services/menu.service';
import { MVLocalStorageService } from './services/mv-local-storage.service';
import { CanDeactivateState } from './infrastructure/route-guards/can-deactivate.resolver';
import { NavigationEnd, Router } from '@angular/router';
import { SwUpdate } from '@angular/service-worker';
import { MatBottomSheet, MatBottomSheetRef, MAT_BOTTOM_SHEET_DATA } from '@angular/material/bottom-sheet';
import { AuthService } from './services/auth.service';

@Component({
    // tslint:disable-next-line: component-selector
    selector: 'app',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
    // tslint:disable-next-line: no-any
    fuseConfig: any;
    // tslint:disable-next-line: no-any
    navigation: any;

    public updateAvailable: boolean;
    loadingUpdates = false;
    unrecoverable: boolean;

    // Private
    private unsubscribeAll: Subject<void>;
    loading = false;
    /**
     * Constructor
     *
     * @param document description
     */
    constructor(
        // tslint:disable-next-line: no-any
        @Inject(DOCUMENT) private document: any,
        private fuseConfigService: FuseConfigService,
        private fuseSidebarService: FuseSidebarService,
        private translate: TranslateService,
        private platform: Platform,
        private menuService: MenuService,
        private router: Router,
        private updates: SwUpdate,
        private bottomSheet: MatBottomSheet,
        private authService: AuthService,
    ) {
        this.authService.isLoggedIn().then(response => {
            if(response) return
            this.fuseConfigService.config = {
              layout: {
                navbar: {
                  hidden: !response
                },
                toolbar: {
                  hidden: !response
                },
                sidepanel: {
                  hidden: !response
                }
              }
            };
        });
        this.updates.available.subscribe(event => {
        if(!this.unrecoverable) {
            this.updateAvailable = true;  // To show only one message
            this.bottomSheet.open(AppUpdateBottomSheetComponent, 
                {   panelClass: 'p-0', 
                    disableClose: true,
                    data: {
                        updateAvailable: this.updateAvailable,
                        unrecoverable: this.unrecoverable
                    }
                });
        }
        });
        this.updates.activated.subscribe(event => {
            console.log('old version was', event.previous);
            console.log('new version is', event.current);
        });
        this.updates.unrecoverable.subscribe(event => {
            this.unrecoverable = true;
            this.updateAvailable = false; //To show only one message
            this.bottomSheet.open(AppUpdateBottomSheetComponent, 
                {   panelClass: 'p-0', 
                    disableClose: true,
                    data: {
                        updateAvailable: this.updateAvailable,
                        unrecoverable: this.unrecoverable
                    }
                });
        });
    
        this.startUpdateChecking();

        if (MVLocalStorageService.getItem('current_user')) {
            this.loading =true
        }
        
        // Get default navigation
        this.navigation = navigation;

        // Register the navigation to the service
        // this._fuseNavigationService.register('main', this.navigation);

        // Set the main navigation as our current navigation
        // this._fuseNavigationService.setCurrentNavigation('main');

        this.menuService.init();

        // Add languages
        this.translate.addLangs(['en-US', 'es-ES']);

        // Set the default language
        this.translate.setDefaultLang('en-US');

        // Set the navigation translations
        // this._fuseTranslationLoaderService.loadTranslations(navigationEnglish, navigationTurkish);

        // Use a language
        const lang = MVLocalStorageService.getItem('selected_lang');
        if (lang) {
            lang.replace('"', '', 'g');
            this.translate.use(lang);
        } else {
            this.translate.use('1');
        }
        //  console.log(this._translateService.getLangs());

        /**
         * ----------------------------------------------------------------------------------------------------
         * ngxTranslate Fix Start
         * ----------------------------------------------------------------------------------------------------
         */

        /**
         * If you are using a language other than the default one, i.e. Turkish in this case,
         * you may encounter an issue where some of the components are not actually being
         * translated when your app first initialized.
         *
         * This is related to ngxTranslate module and below there is a temporary fix while we
         * are moving the multi language implementation over to the Angular's core language
         * service.
         */

        // Set the default language to 'en' and then back to 'tr'.
        // '.use' cannot be used here as ngxTranslate won't switch to a language that's already
        // been selected and there is no way to force it, so we overcome the issue by switching
        // the default language back and forth.
        // setTimeout(() => {
        //     this._translateService.setDefaultLang('en');
        //     this._translateService.setDefaultLang('tr');
        // });

        /**
         * ----------------------------------------------------------------------------------------------------
         * ngxTranslate Fix End
         * ----------------------------------------------------------------------------------------------------
         */

        // Set the private defaults
        this.unsubscribeAll = new Subject();

        // Add is-mobile class to the body if the platform is mobile
        if (this.platform.ANDROID || this.platform.IOS) {
            this.document.body.classList.add('is-mobile');
        }


        // region Setup for canDeactivate guard
        // if the user clicks the back button, ask the CanDeactivateGuard to defend against this.
        window.onpopstate = () => CanDeactivateState.defendAgainstBrowserBackButton = true;

        // Upon successful navigation, ensure that the CanDeactivateGuard no longer defends against back button clicks
        this.router.events.pipe(
            filter(e => e instanceof NavigationEnd),
            tap(() => CanDeactivateState.defendAgainstBrowserBackButton = false)
        ).subscribe();
        // endregion

        // Updates pivot grid internal labels
        // this.translate.getTranslation('es-ES').subscribe({
        //     next: locale => {
        //         L10n.load({
        //             'es-ES': {
        //                 ...locale.PIVOT_GRID
        //             }
        //         });
        //     }
        // });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Subscribe to config changes
        this.fuseConfigService.config
            .pipe(takeUntil(this.unsubscribeAll))
            .subscribe((config) => {

                this.fuseConfig = config;

                // Boxed
                if (this.fuseConfig.layout.width === 'boxed') {
                    this.document.body.classList.add('boxed');
                } else {
                    this.document.body.classList.remove('boxed');
                }

                // Color theme - Use normal for loop for IE11 compatibility
                // tslint:disable-next-line: prefer-for-of
                for (let i = 0; i < this.document.body.classList.length; i++) {
                    const className = this.document.body.classList[i];

                    if (className.startsWith('theme-')) {
                        this.document.body.classList.remove(className);
                    }
                }

                // this.document.body.classList.add(this.fuseConfig.colorTheme); //TODO: Rehabilitar con Tema PwC
            });
    }

    ngAfterViewInit(): void {
        // this.updates.available.subscribe(() => {
        //     console.log("New version detected - asking to install");
        //     this.bottomSheet.open(AppUpdateBottomSheetComponent, { panelClass: 'p-0', disableClose: true });
        // });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this.unsubscribeAll.next(void 0);
        this.unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    toggleSidebarOpen(key): void {
        this.fuseSidebarService.getSidebar(key).toggleOpen();
    }
    
    startUpdateChecking() {
        var hidden, visibilityChange;
        if (typeof document.hidden !== "undefined") {
            hidden = "hidden";
            visibilityChange = "visibilitychange";
            // @ts-ignore
        } else if (typeof document.mozHidden !== "undefined") {
            hidden = "mozHidden";
            visibilityChange = "mozvisibilitychange";
            // @ts-ignore
        } else if (typeof document.msHidden !== "undefined") {
            hidden = "msHidden";
            visibilityChange = "msvisibilitychange";
            // @ts-ignore
        } else if (typeof document.webkitHidden !== "undefined") {
            hidden = "webkitHidden";
            visibilityChange = "webkitvisibilitychange";
        }

        document.addEventListener(visibilityChange, () => this.handleVisibilityChange(), false);
    }

    async handleVisibilityChange() {
        if (this.updates.isEnabled && document.visibilityState === 'visible' && !this.loadingUpdates) {
            this.loadingUpdates = true;
            await this.updates.checkForUpdate();
            this.loadingUpdates = false;
        }
    }
}

@Component({
    selector: 'app-update-bottom-sheet',
    template: `<div class="accent p-16 text-center" style="cursor: pointer;" (click)="applyUpdate()">
                <span>{{'UPDATE-AVAILABLE' | translate}}</span>
                </div>`
})
export class AppUpdateBottomSheetComponent {
    updateAvailable: boolean;
    unrecoverable: boolean;

    constructor(
        private updates: SwUpdate,
        public bottomSheetRef: MatBottomSheetRef<AppUpdateBottomSheetComponent>,
        @Inject(MAT_BOTTOM_SHEET_DATA) public data?: {updateAvailable: boolean, unrecoverable: boolean},
    ) {
        this.updateAvailable = data.updateAvailable;
        this.unrecoverable = data.unrecoverable;
     }

    applyUpdate() {
        console.log("Installing new version");

        if (this.updateAvailable) {
            return this.updates.activateUpdate()
                .then(() => {
                    console.log("New version installed - reloading page");
                    document.location.reload();
                });
        }
        if (this.unrecoverable) {
            location.href = '/refresh.html';
        }
    }
}
